import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAtomValue } from "jotai";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  isBrainCatalogEntry,
  isBrainPoolEntry,
  resolvePiWireRow,
  parseModelSelection,
  rememberReviewedModels,
  resolveBrainSelectionAgainstCandidates,
  describeUnavailableReason,
  type BrainSelectionResolution,
  type EnterpriseAdminBundleRaw,
  type EnterpriseAdminBundleRawModel,
  type EnterpriseAdminBundleOverrideRedacted,
  type EnterpriseAdminBundleGroupOverridePatch,
  type EnterpriseAdminBroadcastResult,
  type EnterpriseBundleMember,
  type MemoryDistillRunStatus,
  type ModelSelection,
  type ModelParamsByChain,
} from "@dosia/shared";
import { apiCall } from "../../lib/api-client.js";
import { ApiError, canManageOrg, type PublicRole } from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import { useApiQuery } from "../../lib/useApiQuery.js";
import {
  modelsRawPath,
  modelsOverridePath,
  modelsBroadcastPath,
  modelsTakeoverPath,
  modelsMembersPath,
  modelSelectionPath,
  modelsMemoryEnabledPath,
  modelsCloudTaskBudgetPath,
  modelsMonitorAiDailyLimitPath,
  memoryDistillAllPath,
  memoryDistillStatusPath,
  rolesPath,
  roleByIdPath,
} from "../../lib/api-paths.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";
import { StatusPill } from "../../components/StatusPill.js";
import { FormField, PrimaryButton, SecondaryButton, TextInput } from "../../components/FormField.js";
import { Modal } from "../../components/Modal.js";
import { ChainParamRangeEditor } from "../../components/ChainParamRangeEditor.js";
import { cn } from "../../lib/cn.js";
import { SeatOverviewTab } from "./SeatOverviewTab.js";
import { CloudTaskModelListCard } from "./CloudTaskModelListCard.js";
import type { OrgDetailContext } from "./context.js";

/**
 * 企业模型管理 — 企业管理员在管理后台接管 ByteSpike org、覆盖 org 级模型凭证、广播下发全员、
 * 监控成员同步状态。从桌面端 ModelManagementTab 迁来（app 端 tab 后续统一清理）。
 * dosia-enterprise 服务间通道透传（/models，org-admin + super/support 可管理）：保存/下发由服务端加密下发
 * 全员桌面端，员工下个新对话即生效。业务单一真相源在 dosia-enterprise，本页只做状态渲染 + 操作。
 *
 * bundle / override 的数据形状是跨端序列化结构（红线 #9），typed 定义在 @dosia/shared，
 * 本页 import 复用而非另 declare。个人/本地模式模型配置不在本页（保留桌面端 app）。
 */

/** GET /raw：接管则返 raw bundle；未接管返 null；通道未配置返 sentinel。 */
type RawResponse =
  | EnterpriseAdminBundleRaw
  | { channelDisabled: true; bundle: null }
  | null;
type OverrideResponse =
  | EnterpriseAdminBundleOverrideRedacted
  | { channelDisabled: true; override: null }
  | null;
type MembersResponse = { members: EnterpriseBundleMember[]; channelDisabled?: boolean };

/** 接管 import 结果（DosiaOrgImportResult 桌面私有，此处只读需要的字段）。 */
interface TakeoverResult {
  imported: boolean;
  orgCodeLabel: string;
  modelCount: number;
  pushVersion: number;
}

type OpResult = { kind: "ok"; text: string } | { kind: "error"; text: string };

function isChannelDisabled(
  data: RawResponse | OverrideResponse | MembersResponse | null,
): boolean {
  return !!data && typeof data === "object" && "channelDisabled" in data && data.channelDisabled === true;
}

function OpBanner({ result }: { result: OpResult }): JSX.Element {
  if (result.kind === "error") return <ErrorBanner>{result.text}</ErrorBanner>;
  return (
    <div className="rounded-md border border-emerald-400/40 bg-emerald-400/10 px-3 py-2 text-sm text-emerald-700">
      {result.text}
    </div>
  );
}

function errText(err: unknown): string {
  return err instanceof ApiError ? err.message : "操作失败，请稍后重试。";
}

// ---------- 每组模型覆盖的有效底值（override > raw） ----------

interface GroupRow {
  name: string;
  /** raw ByteSpike bundle 的底值 baseUrl（override 缺省时的生效值）。 */
  rawBaseUrl: string;
  /** raw apiKey 掩码（后缀，非明文）。 */
  rawApiKeyMask: string;
}

function deriveGroups(raw: EnterpriseAdminBundleRaw): GroupRow[] {
  const byName = new Map<string, GroupRow>();
  for (const g of raw.entitledGroups ?? []) {
    if (!byName.has(g.name)) byName.set(g.name, { name: g.name, rawBaseUrl: "", rawApiKeyMask: "" });
  }
  for (const m of raw.models) {
    const name = m.chosenGroup?.name;
    if (!name) continue;
    const existing = byName.get(name);
    if (!existing) {
      byName.set(name, { name, rawBaseUrl: m.baseUrl, rawApiKeyMask: m.apiKey });
    } else {
      if (!existing.rawBaseUrl) existing.rawBaseUrl = m.baseUrl;
      if (!existing.rawApiKeyMask) existing.rawApiKeyMask = m.apiKey;
    }
  }
  return [...byName.values()];
}

/** 网关对双协议模型（如 kimi-k3）按 protocol 下发两行；治理表按 modelId 折叠为一行。
 *  主行优先 anthropic_messages；capabilities 布尔字段逐个 OR 合并、capabilityTags 取并集，
 *  防两协议行能力面不一致时丢 chip（其余非布尔字段如 contextWindow/thinkingParam 取主行）。
 *  原始双行语义（计价/路由）保留在服务端 raw 视图，本 helper 只影响治理表渲染与「共 N 个模型」计数。 */
export function dedupeModelsById(
  models: EnterpriseAdminBundleRawModel[],
): EnterpriseAdminBundleRawModel[] {
  // Map 覆盖已有 key 时保持原插入位置 → values() 天然是首次出现顺序。
  const byId = new Map<string, EnterpriseAdminBundleRawModel>();
  for (const m of models) {
    const existing = byId.get(m.modelId);
    byId.set(m.modelId, existing ? mergeModelRows(existing, m) : m);
  }
  return [...byId.values()];
}

function mergeModelRows(
  a: EnterpriseAdminBundleRawModel,
  b: EnterpriseAdminBundleRawModel,
): EnterpriseAdminBundleRawModel {
  const primary = a.protocol === "anthropic_messages" ? a : b.protocol === "anthropic_messages" ? b : a;
  const secondary = primary === a ? b : a;
  return {
    ...primary,
    capabilities: mergeCapabilities(primary.capabilities, secondary.capabilities),
    capabilityTags: [...new Set([...(primary.capabilityTags ?? []), ...(secondary.capabilityTags ?? [])])],
  };
}

function mergeCapabilities(
  a: EnterpriseAdminBundleRawModel["capabilities"],
  b: EnterpriseAdminBundleRawModel["capabilities"],
): EnterpriseAdminBundleRawModel["capabilities"] {
  if (!a) return b;
  if (!b) return a;
  return {
    ...a,
    supportsVision: !!a.supportsVision || !!b.supportsVision,
    supportsVideo: !!a.supportsVideo || !!b.supportsVideo,
    supportsToolUse: !!a.supportsToolUse || !!b.supportsToolUse,
    supportsThinking: !!a.supportsThinking || !!b.supportsThinking,
    supports1MContext: !!a.supports1MContext || !!b.supports1MContext,
    supportsEffort: !!a.supportsEffort || !!b.supportsEffort,
    supportsAutoMode: !!a.supportsAutoMode || !!b.supportsAutoMode,
  };
}

/** 轻量「上游可用/不可用」标记（企业管理员简化显示）。available 来自服务端跨协议聚合
 *  （aggregateModelAvailability）。**三态**：
 *   - false → amber 小点 + 简短原因（describeUnavailableReason humanize，单一 humanizer 源）
 *   - true  → 中性绿点 +「可用」（正向确认，不喧宾夺主）
 *   - undefined（契约未部署 / full catalog 缺该模型）→ 不标记；`dash` 时占位「—」。
 *  非 bug 不红色：不可用走 amber（协议约束/上游态，非本地故障）。 */
function AvailabilityMark({
  model,
  dash,
  id,
}: {
  model: EnterpriseAdminBundleRawModel;
  dash?: boolean;
  id?: string;
}): JSX.Element | null {
  if (model.available === false) {
    const reason = describeUnavailableReason(model.unavailableReason);
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-amber-700"
        title={`上游暂不可用 · ${reason}`}
        id={id}
        data-testid="availability-unavailable"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden />
        上游暂不可用 · {reason}
      </span>
    );
  }
  if (model.available === true) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-dosia-text-dim"
        data-testid="availability-available"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden />
        可用
      </span>
    );
  }
  return dash ? <span className="text-dosia-text-dim">—</span> : null;
}

// ---------- 1. ByteSpike 接管 ----------

function TakeoverCard({
  orgCode,
  token,
  bundle,
  onChanged,
}: {
  orgCode: string;
  token: string;
  bundle: EnterpriseAdminBundleRaw | null;
  onChanged: () => void;
}): JSX.Element {
  const [bytespikeOrg, setBytespikeOrg] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<OpResult | null>(null);
  const imported = !!bundle;

  const takeover = async (): Promise<void> => {
    if (!bytespikeOrg.trim() || busy) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await apiCall<TakeoverResult>("POST", modelsTakeoverPath(orgCode), {
        token,
        body: { orgCode: bytespikeOrg.trim() },
      });
      setResult({
        kind: "ok",
        text: `已接管「${r.orgCodeLabel}」，同步 ${r.modelCount} 个模型（版本 v${r.pushVersion}）。`,
      });
      setBytespikeOrg("");
      onChanged();
    } catch (err) {
      setResult({ kind: "error", text: errText(err) });
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (): Promise<void> => {
    if (busy) return;
    if (!window.confirm("撤销接管后将向全员下发清空配置，员工桌面端企业模型会移除。确认撤销？")) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await apiCall<{ ok: boolean; broadcastCount?: number }>(
        "DELETE",
        modelsTakeoverPath(orgCode),
        { token },
      );
      setResult({ kind: "ok", text: `已撤销接管，已通知 ${r.broadcastCount ?? 0} 名员工。` });
      onChanged();
    } catch (err) {
      setResult({ kind: "error", text: errText(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-dosia-border p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-medium text-dosia-text">ByteSpike 接管</h3>
        {imported ? (
          <StatusPill status="connected" label="已接管 ✓" />
        ) : (
          <StatusPill status="disconnected" label="未接管" />
        )}
      </div>
      {result && <OpBanner result={result} />}
      {imported && bundle ? (
        <div className="space-y-3">
          <p className="text-xs text-dosia-text-secondary leading-relaxed">
            当前接管：<span className="font-medium text-dosia-text">{bundle.orgCodeLabel}</span>，
            共 {dedupeModelsById(bundle.models).length} 个模型，配置版本 v{bundle.pushVersion}
            （账号 <span className="font-mono">{bundle.pushedByAccount}</span>）。
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => void revoke()}
            className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-sm text-amber-700 hover:bg-amber-500/20 disabled:opacity-50"
          >
            {busy ? "处理中…" : "撤销接管"}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-dosia-text-secondary leading-relaxed">
            输入 ByteSpike 组织 code 接管本企业的模型网关配置。接管后由服务端拉取模型清单并加密下发全员，
            员工登录时自动注册子账号。
          </p>
          <FormField label="ByteSpike 组织 code">
            <TextInput
              value={bytespikeOrg}
              onChange={(e) => setBytespikeOrg(e.target.value)}
              placeholder="例如 catalyst-prod"
              className="font-mono"
            />
          </FormField>
          <PrimaryButton disabled={!bytespikeOrg.trim() || busy} onClick={() => void takeover()}>
            {busy ? "接管中…" : "接管"}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
}

// ---------- 2. 模型 override 编辑 ----------

function OverrideEditor({
  orgCode,
  token,
  bundle,
  override,
  onSaved,
}: {
  orgCode: string;
  token: string;
  bundle: EnterpriseAdminBundleRaw;
  override: EnterpriseAdminBundleOverrideRedacted | null;
  onSaved: () => void;
}): JSX.Element {
  const groups = deriveGroups(bundle);
  const [baseUrls, setBaseUrls] = useState<Record<string, string>>({});
  const [apiKeys, setApiKeys] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<OpResult | null>(null);

  // baseUrl 预填有效底值（override > raw）；apiKey 永远留空（掩码只作 placeholder）。
  useEffect(() => {
    const init: Record<string, string> = {};
    for (const g of groups) {
      init[g.name] = override?.groupOverrides?.[g.name]?.baseUrl || g.rawBaseUrl || "";
    }
    setBaseUrls(init);
    // groups 由 bundle 派生，bundle 变化即重置。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundle, override]);

  const save = async (): Promise<void> => {
    if (busy) return;
    const groupOverrides: Record<string, EnterpriseAdminBundleGroupOverridePatch> = {};
    for (const g of groups) {
      const baseUrl = (baseUrls[g.name] ?? "").trim();
      const apiKey = (apiKeys[g.name] ?? "").trim();
      const patch: EnterpriseAdminBundleGroupOverridePatch = {};
      if (baseUrl) patch.baseUrl = baseUrl;
      if (apiKey) patch.apiKey = apiKey; // 空 apiKey 省略 → 服务端沿用当前值
      if (Object.keys(patch).length > 0) groupOverrides[g.name] = patch;
    }
    if (Object.keys(groupOverrides).length === 0) {
      setResult({ kind: "error", text: "没有可保存的覆盖（至少填一组 baseUrl 或 API Key）。" });
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const r = await apiCall<{ editedAt: string; groupCount: number }>(
        "PUT",
        modelsOverridePath(orgCode),
        { token, body: { groupOverrides } },
      );
      setResult({ kind: "ok", text: `已保存 ${r.groupCount} 组覆盖。点「广播下发」推送给全员。` });
      setApiKeys({});
      onSaved();
    } catch (err) {
      setResult({ kind: "error", text: errText(err) });
    } finally {
      setBusy(false);
    }
  };

  const clearAll = async (): Promise<void> => {
    if (busy) return;
    if (!window.confirm("清除全部覆盖后将复位到 ByteSpike 原始配置（需再广播下发生效）。确认清除？")) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await apiCall<{ clearedGroups: string[] }>("DELETE", modelsOverridePath(orgCode), { token });
      setResult({ kind: "ok", text: `已清除 ${r.clearedGroups.length} 组覆盖，复位到原始配置。` });
      setApiKeys({});
      onSaved();
    } catch (err) {
      setResult({ kind: "error", text: errText(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-dosia-border p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-medium text-dosia-text">模型 override 编辑</h3>
        <span className="text-xs text-dosia-text-dim">
          {override ? `上次编辑 ${override.editedBy}` : "未设置覆盖"}
        </span>
      </div>
      <p className="text-xs text-dosia-text-secondary leading-relaxed">
        按分组覆盖 org 级的 baseUrl / API Key（留空 API Key ＝沿用当前值，绝不回显明文）。保存后点「广播下发」推送给全员。
      </p>
      {result && <OpBanner result={result} />}
      <div className="space-y-4">
        {groups.map((g) => {
          const ov = override?.groupOverrides?.[g.name];
          const keyPlaceholder = ov?.apiKeySet && ov.apiKeyMask
            ? `留空＝沿用当前值 ${ov.apiKeyMask}`
            : g.rawApiKeyMask
              ? `留空＝沿用原始值 ${g.rawApiKeyMask}`
              : "填写 API Key（留空＝沿用当前值）";
          return (
            <div key={g.name} className="rounded-md border border-dosia-border/60 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-dosia-text">{g.name}</span>
                {ov && <StatusPill status="connected" label="已覆盖" />}
              </div>
              <FormField label="Base URL">
                <TextInput
                  value={baseUrls[g.name] ?? ""}
                  onChange={(e) => setBaseUrls((s) => ({ ...s, [g.name]: e.target.value }))}
                  placeholder={g.rawBaseUrl || "https://…/v1"}
                  className="font-mono"
                />
              </FormField>
              <FormField label="API Key">
                <TextInput
                  type="password"
                  value={apiKeys[g.name] ?? ""}
                  onChange={(e) => setApiKeys((s) => ({ ...s, [g.name]: e.target.value }))}
                  placeholder={keyPlaceholder}
                  autoComplete="off"
                  className="font-mono"
                />
              </FormField>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <PrimaryButton disabled={busy} onClick={() => void save()}>
          {busy ? "保存中…" : "保存覆盖"}
        </PrimaryButton>
        {override && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void clearAll()}
            className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-sm text-amber-700 hover:bg-amber-500/20 disabled:opacity-50"
          >
            清除全部覆盖
          </button>
        )}
      </div>
    </div>
  );
}

// ---------- 3. 广播下发 ----------

function BroadcastCard({
  orgCode,
  token,
}: {
  orgCode: string;
  token: string;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<OpResult | null>(null);

  const broadcast = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await apiCall<EnterpriseAdminBroadcastResult>("POST", modelsBroadcastPath(orgCode), {
        token,
        body: { kind: "catalog" },
      });
      setResult({
        kind: "ok",
        text: `已下发 ${r.pushed ?? 0}/${r.memberCount ?? 0} 名员工${r.failed ? `（${r.failed} 名失败）` : ""}。成员下个新对话即生效。`,
      });
    } catch (err) {
      setResult({ kind: "error", text: errText(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-dosia-border p-4 space-y-3">
      <h3 className="text-sm font-medium text-dosia-text">广播下发</h3>
      <p className="text-xs text-dosia-text-secondary leading-relaxed">
        把当前生效配置（raw bundle + override）全量下发给所有已注册员工。改了模型清单或凭证后点此推送。
      </p>
      {result && <OpBanner result={result} />}
      <PrimaryButton disabled={busy} onClick={() => void broadcast()}>
        {busy ? "下发中…" : "广播下发全员"}
      </PrimaryButton>
    </div>
  );
}

// ---------- 4. 成员监控 ----------

function MembersTable({ members }: { members: EnterpriseBundleMember[] }): JSX.Element {
  return (
    <div className="rounded-lg border border-dosia-border p-4 space-y-3">
      <h3 className="text-sm font-medium text-dosia-text">成员监控</h3>
      {members.length === 0 ? (
        <p className="text-sm text-dosia-text-dim">暂无已注册成员。</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-dosia-text-dim border-b border-dosia-border">
                <th className="py-1.5 pr-3 font-medium">员工</th>
                <th className="py-1.5 pr-3 font-medium">状态</th>
                <th className="py-1.5 pr-3 font-medium">配置版本</th>
                <th className="py-1.5 pr-3 font-medium">最近同步</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.employeeId} className="border-b border-dosia-border/50">
                  <td className="py-1.5 pr-3 font-mono text-dosia-text">{m.employeeId}</td>
                  <td className="py-1.5 pr-3">
                    {m.lastError ? (
                      <StatusPill status="disconnected" label="同步异常" />
                    ) : m.online ? (
                      <StatusPill status="connected" label="在线" />
                    ) : (
                      <StatusPill status="disconnected" label="离线" />
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-dosia-text-secondary">v{m.lastAppliedVersion}</td>
                  <td className="py-1.5 pr-3 text-dosia-text-secondary">
                    {m.lastFetchedAt ? new Date(m.lastFetchedAt).toLocaleString("zh-CN") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------- 5. 记忆治理 / 模型治理统一面 ----------

/** 组织级记忆开关默认（P2）——独立于 ByteSpike 接管（记忆治理不依赖网关），故渲染在
 *  bundle 早退之前，始终可见。缺省开；关闭后全组织员工记忆被强制关（角色级另可更严）。 */
function OrgMemoryToggle({ orgCode, token }: { orgCode: string; token: string }): JSX.Element {
  const memQ = useApiQuery<{ orgMemoryEnabled: boolean }>(
    modelsMemoryEnabledPath(orgCode),
    [orgCode],
  );
  const [local, setLocal] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (memQ.data) setLocal(memQ.data.orgMemoryEnabled);
  }, [memQ.data]);

  const enabled = local ?? true;

  const toggle = async (next: boolean): Promise<void> => {
    const prev = local;
    setLocal(next); // 乐观
    setBusy(true);
    setSaveError(null);
    try {
      const r = await apiCall<{ orgMemoryEnabled: boolean }>(
        "PUT",
        modelsMemoryEnabledPath(orgCode),
        { token, body: { orgMemoryEnabled: next } },
      );
      setLocal(r.orgMemoryEnabled);
    } catch (err) {
      setLocal(prev ?? true); // 回滚
      setSaveError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-dosia-border p-4 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-dosia-text">组织记忆</h3>
          <p className="text-xs text-dosia-text-secondary leading-relaxed">
            DOSIA 记忆是否对全组织员工启用（缺省开）。关闭后全员记忆被强制关闭、员工无法自行打开；
            按角色更细控制请到该角色的策略编辑器。员工下个新对话即生效。
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
          <input
            type="checkbox"
            checked={enabled}
            disabled={busy || (memQ.loading && !memQ.data)}
            onChange={(e) => void toggle(e.target.checked)}
            aria-label="org-memory-enabled"
          />
          <span className={cn("text-xs", enabled ? "text-dosia-text-dim" : "text-amber-700")}>
            {enabled ? "已启用" : "已关闭"}
          </span>
        </label>
      </div>
      {saveError && <ErrorBanner onDismiss={() => setSaveError(null)}>{saveError}</ErrorBanner>}
    </div>
  );
}

/** 组织级云任务告警额度（2026-07-10）——正数=每员工每北京日累计云端任务模型花费的告警阈值
 *  （N-27：modelCall 每次尝试按实际花费补差，agentTurn 按单次上限预留估算；实际账单以网关为准），
 *  留空=不设组织级额度。用户 modelCall / agentTurn 超额仍执行并告警管理员，script 不计入；
 *  记忆整理/全员蒸馏仍会硬拦。角色级 managedSettings.cloudTaskDailyBudgetUsd 与组织级收严取小。 */
function OrgCloudTaskBudget({ orgCode, token }: { orgCode: string; token: string }): JSX.Element {
  const budgetQ = useApiQuery<{ cloudTaskDailyBudgetUsd: number | null }>(
    modelsCloudTaskBudgetPath(orgCode),
    [orgCode],
  );
  const [local, setLocal] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [invalidHint, setInvalidHint] = useState<string | null>(null);

  useEffect(() => {
    if (budgetQ.data) {
      setLocal(
        typeof budgetQ.data.cloudTaskDailyBudgetUsd === "number"
          ? String(budgetQ.data.cloudTaskDailyBudgetUsd)
          : "",
      );
    }
  }, [budgetQ.data]);

  const save = async (): Promise<void> => {
    const trimmed = local.trim();
    let next: number | null;
    if (!trimmed) {
      next = null;
    } else {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n <= 0) {
        setInvalidHint("必须是正数（美元），留空表示不设组织级额度");
        return;
      }
      next = n;
    }
    setInvalidHint(null);
    setBusy(true);
    setSaveError(null);
    try {
      const r = await apiCall<{ cloudTaskDailyBudgetUsd: number | null }>(
        "PUT",
        modelsCloudTaskBudgetPath(orgCode),
        { token, body: { cloudTaskDailyBudgetUsd: next } },
      );
      setLocal(typeof r.cloudTaskDailyBudgetUsd === "number" ? String(r.cloudTaskDailyBudgetUsd) : "");
    } catch (err) {
      setSaveError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-dosia-border p-4 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-dosia-text">每员工每日云任务告警额度（USD）</h3>
          <p className="text-xs text-dosia-text-secondary leading-relaxed">
            按每位员工、每个北京日累计云端任务的模型花费：只调一次模型的任务按实际花费补差，完整对话类任务按单次上限预留估算（实际账单以网关为准）；留空表示不设组织级额度。
            只调一次模型的任务 / 完整对话类任务超额后仍照常执行，并告警管理员；script 不计入。
            夜间记忆整理与全员蒸馏超额时会被拦截。次日自动重新累计；角色级可配置更严格额度。
          </p>
        </div>
        <div className="flex items-center gap-2 whitespace-nowrap">
          <TextInput
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={() => void save()}
            placeholder="不限"
            disabled={busy || (budgetQ.loading && !budgetQ.data)}
            aria-label="org-cloud-task-budget"
            className="w-24"
          />
          <SecondaryButton onClick={() => void save()} disabled={busy}>
            {busy ? "保存中…" : "保存"}
          </SecondaryButton>
        </div>
      </div>
      {invalidHint && <p className="text-xs text-amber-700">{invalidHint}</p>}
      {saveError && <ErrorBanner onDismiss={() => setSaveError(null)}>{saveError}</ErrorBanner>}
    </div>
  );
}

/** 组织级盯盘 AI 分析次数上限（N-57 期 4 D4-03）——与 OrgCloudTaskBudget 同构：一个数字输入 + 保存。
 *  非负整数 = 每员工每北京日 AI 解读 / 简报摘要 / 巡检合计上限，0 = 关闭；留空 = 默认 20。
 *  员工侧看不到次数与原因（超过后只收数字提醒）。文案走管理台 `admin.monitorAiLimit.*`（7 份 locale）。 */
function OrgMonitorAiDailyLimit({ orgCode, token }: { orgCode: string; token: string }): JSX.Element {
  const { t } = useTranslation();
  const limitQ = useApiQuery<{ monitorAiDailyLimit: number | null }>(
    modelsMonitorAiDailyLimitPath(orgCode),
    [orgCode],
  );
  // 不在 effect 里把服务端值抄进本地状态：输入框显示「正在编辑的草稿 ?? 最近一次保存回显 ?? 服务端值」。
  const [draft, setDraft] = useState<string | null>(null);
  const [savedLimit, setSavedLimit] = useState<number | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);
  const currentLimit = savedLimit !== undefined ? savedLimit : (limitQ.data?.monitorAiDailyLimit ?? null);
  const local = draft ?? (typeof currentLimit === "number" ? String(currentLimit) : "");

  const save = async (): Promise<void> => {
    const trimmed = local.trim();
    let next: number | null;
    if (!trimmed) {
      next = null;
    } else {
      const n = Number(trimmed);
      if (!/^\d+$/.test(trimmed) || !Number.isSafeInteger(n)) {
        setInvalid(true);
        return;
      }
      next = n;
    }
    setInvalid(false);
    setBusy(true);
    setSaveError(null);
    try {
      const r = await apiCall<{ monitorAiDailyLimit: number | null }>(
        "PUT",
        modelsMonitorAiDailyLimitPath(orgCode),
        { token, body: { monitorAiDailyLimit: next } },
      );
      setSavedLimit(typeof r.monitorAiDailyLimit === "number" ? r.monitorAiDailyLimit : null);
      setDraft(null);
    } catch (err) {
      setSaveError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-dosia-border p-4 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-dosia-text">{t("admin.monitorAiLimit.title")}</h3>
          <p className="text-xs text-dosia-text-secondary leading-relaxed">{t("admin.monitorAiLimit.hint")}</p>
        </div>
        <div className="flex items-center gap-2 whitespace-nowrap">
          <TextInput
            value={local}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => void save()}
            placeholder={t("admin.monitorAiLimit.placeholder")}
            disabled={busy || (limitQ.loading && !limitQ.data)}
            aria-label="org-monitor-ai-daily-limit"
            inputMode="numeric"
            className="w-24"
          />
          <SecondaryButton onClick={() => void save()} disabled={busy}>
            {t("admin.monitorAiLimit.save")}
          </SecondaryButton>
        </div>
      </div>
      {invalid && <p className="text-xs text-dosia-warning">{t("admin.monitorAiLimit.invalid")}</p>}
      {saveError && <ErrorBanner onDismiss={() => setSaveError(null)}>{saveError}</ErrorBanner>}
    </div>
  );
}

/** 全员蒸馏记忆（P5-B）——admin 一键，服务端逐员工把近期会话蒸馏进【每位员工自己的】私有
 *  记忆。隐私：机器侧处理会话正文，admin 触发但全程不可见内容；产出只入员工 owner-only 私有
 *  记忆。会产生模型调用费用，故弹确认框；后台 job，触发后轮询进度。
 *  `MemoryDistillRunStatus` 单源在 @dosia/shared（红线 #9）——不再手抄字段，跟着
 *  dosia-enterprise memory-distill.ts 的产出走。 */
function AllUserDistillButton({ orgCode, token }: { orgCode: string; token: string }): JSX.Element {
  const statusQ = useApiQuery<MemoryDistillRunStatus | null>(memoryDistillStatusPath(orgCode), [orgCode]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = statusQ.data;
  const running = run?.status === "running";

  // 运行中每 4s 轮询进度；结束即停。
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => void statusQ.refetch(), 4000);
    return () => clearInterval(timer);
  }, [running, statusQ.refetch]);

  const start = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await apiCall("POST", memoryDistillAllPath(orgCode), { token, body: {} });
      setConfirmOpen(false);
      await statusQ.refetch();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-dosia-border p-4 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-dosia-text">全员蒸馏记忆</h3>
          <p className="text-xs text-dosia-text-secondary leading-relaxed">
            让 DOSIA 读取每位员工近期的对话，把值得长期记住的偏好/反馈/项目/资料，蒸馏进
            <b>该员工自己的</b>私有记忆（管理员不会看到任何对话内容）。会产生模型调用费用。
          </p>
        </div>
        <PrimaryButton
          onClick={() => setConfirmOpen(true)}
          disabled={busy || running}
          className="whitespace-nowrap"
        >
          {running ? "蒸馏进行中…" : "发起全员蒸馏"}
        </PrimaryButton>
      </div>
      {run && (
        <p className="text-xs text-dosia-text-dim">
          最近一次：{run.status === "running" ? "进行中" : run.status === "done" ? "已完成" : "失败"}
          {" · "}处理 {run.processed} / 跳过 {run.skipped} / 失败 {run.failed}（共 {run.total} 人）
        </p>
      )}
      {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}
      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="发起全员蒸馏记忆？"
        footer={
          <div className="flex justify-end gap-2">
            <SecondaryButton onClick={() => setConfirmOpen(false)} disabled={busy}>
              取消
            </SecondaryButton>
            <PrimaryButton onClick={() => void start()} disabled={busy}>
              {busy ? "发起中…" : "确认发起"}
            </PrimaryButton>
          </div>
        }
      >
        <p className="text-sm text-dosia-text-secondary leading-relaxed">
          将对全组织员工逐个运行记忆蒸馏，读取各自近期对话并写入其私有记忆。
          <b>此操作会产生模型调用费用</b>（按 token 计费）。7 天内已蒸馏过的员工会自动跳过。
          任务在后台串行执行，可稍后在此查看进度。是否继续？
        </p>
      </Modal>
    </div>
  );
}

/** 记忆治理区：组织记忆开关（P2）+ 全员蒸馏（P5-B），渲染在一起。 */
function MemoryGovernanceSection({ orgCode, token }: { orgCode: string; token: string }): JSX.Element {
  return (
    <>
      <OrgMemoryToggle orgCode={orgCode} token={token} />
      <OrgCloudTaskBudget orgCode={orgCode} token={token} />
      <OrgMonitorAiDailyLimit orgCode={orgCode} token={token} />
      <AllUserDistillButton orgCode={orgCode} token={token} />
    </>
  );
}

/** 记忆治理 tab —— 组织记忆开关 / 云端任务日预算 / 全员蒸馏（正交于模型治理，独立成 tab）。 */
function MemoryTab({ orgCode, token }: { orgCode: string; token: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <MemoryGovernanceSection orgCode={orgCode} token={token} />
    </div>
  );
}

// ---------- 统一「模型治理」tab（三层漏斗中层：scope=全员默认 / 各角色，三态可见性 + 参数） ----------

/**
 * 单线（主脑 / 工具）圈定 —— 三态 UX：
 *  - **未配置态**（local === null）：继承上层全量。全候选打勾 + 「继承中」标记（text-dim，非红），
 *    取消任一项即**物化**成显式白名单并切「已配置」。
 *  - **已配置态**（local === string[]）：正常勾选，可增删。
 *  - **恢复继承**：清回 null（该线重新继承上层）。
 */
function CurationLine({
  kind,
  title,
  hint,
  candidates,
  local,
  onChange,
  ariaPrefix = "org-model",
  renderRowExtra,
}: {
  kind: "brain" | "tool";
  title: string;
  /** 覆盖默认「继承平台全量」文案（角色 scope 时候选来自 org 圈定集，非平台全量）。 */
  hint?: (inherited: boolean, checked: number, total: number) => string;
  candidates: EnterpriseAdminBundleRawModel[];
  /** null = 未配置(继承) · string[] = 已显式圈定的 base id 集。 */
  local: string[] | null;
  onChange: (next: string[] | null) => void;
  /** aria-label 前缀（org scope=org-model，role scope=role-model，避免同页双 scope 撞 label）。 */
  ariaPrefix?: string;
  /** 每行下方额外内容（brain 参数折叠编辑器）——tool 线不传即无。 */
  renderRowExtra?: (model: EnterpriseAdminBundleRawModel) => ReactNode;
}): JSX.Element {
  const inherited = local === null;
  const allIds = candidates.map((m) => m.modelId);
  const checkedSet = new Set(inherited ? allIds : local);

  const toggle = (modelId: string, checked: boolean): void => {
    // 未配置态首次交互 → 物化成当前显式全集，再应用本次开关（继承 footgun 防护对称）。
    const baseIds = inherited ? allIds : (local ?? []);
    const next = checked
      ? [...new Set([...baseIds, modelId])]
      : baseIds.filter((id) => id !== modelId);
    onChange(next);
  };

  const defaultHint = inherited
    ? `继承平台全量（${candidates.length} 个）· 取消任一项即切换为本企业显式圈定`
    : `本企业显式圈定 ${checkedSet.size}/${candidates.length} 个 ·「恢复继承」清空则回落上层全量`;

  return (
    <div className="rounded-md border border-dosia-border p-3 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-dosia-text">
          {title}
          {inherited ? (
            <span className="ml-2 rounded bg-dosia-bg-lighter px-1.5 py-0.5 text-xs font-normal text-dosia-text-dim">
              继承中
            </span>
          ) : null}
        </p>
        {!inherited ? (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs text-dosia-accent hover:underline"
            aria-label={`restore-inherit-${kind}`}
          >
            恢复继承
          </button>
        ) : null}
      </div>
      <p className="text-xs text-dosia-text-dim">
        {hint ? hint(inherited, checkedSet.size, candidates.length) : defaultHint}
      </p>
      {candidates.length === 0 ? (
        <p className="text-xs text-dosia-text-dim">无候选模型</p>
      ) : (
        <div className="flex flex-col gap-1.5">
          {candidates.map((m) => {
            const checked = checkedSet.has(m.modelId);
            const unavailable = m.available === false;
            const availabilityId = `org-model-unavailable-${kind}-${m.modelId.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
            return (
              <div key={m.modelId} className="flex flex-col gap-1">
                <label className={cn("flex items-center gap-2 text-sm", unavailable && !checked ? "cursor-not-allowed" : "cursor-pointer")}>
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={unavailable && !checked}
                    onChange={(e) => toggle(m.modelId, e.target.checked)}
                    aria-label={`${ariaPrefix}-${kind}-${m.modelId}`}
                    aria-describedby={unavailable ? availabilityId : undefined}
                    title={unavailable ? `上游暂不可用 · ${describeUnavailableReason(m.unavailableReason)}` : undefined}
                  />
                  <span className={cn(inherited ? "text-dosia-text-dim" : "text-dosia-text")}>
                    {m.displayName || m.modelId}
                  </span>
                  <span className="font-mono text-xs text-dosia-text-dim">{m.modelId}</span>
                  <AvailabilityMark model={m} id={availabilityId} />
                </label>
                {renderRowExtra?.(m)}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------- 参数范围编辑器接线（复用组件 ChainParamRangeEditor，红线 #9 单组件） ----------

/** 剥离 [1m] 等 suffix tag → base id（modelParams map 的 key，与服务端
 *  buildModelSelection/resolvePerChainParams 的 stripModelSuffixTag 同口径）。 */
function baseModelId(modelId: string): string {
  return modelId.replace(/\[[^\]]*\]/g, "");
}

type ModelCaps = EnterpriseAdminBundleRawModel["capabilities"];

/**
 * per-chain 协议专属能力天花板 lookup（方案 §2，Codex 修 #6）——从**未折叠的** raw bundle.models
 * 建 base → {sdk, pi} caps：SDK 段取 `anthropic_messages` 行 caps，Pi 段取清单为该模型**选中的那条
 * pi 接口行**（shared resolvePiWireRow，N-121 批次 B，与员工端 route 同一行）caps。**禁用 dedupeModelsById
 * 折叠后的主行当两链共同能力**（折叠取 anthropic 主行的非布尔字段 thinkingParam/outputTokenParam，
 * 会漏 Pi 专属能力）。SDK 段同协议多行取首个带 capabilities 的行。 */
function buildChainCapsByBase(
  rawModels: EnterpriseAdminBundleRawModel[] | undefined,
): Map<string, { sdk: ModelCaps; pi: ModelCaps }> {
  const rows = rawModels ?? [];
  const map = new Map<string, { sdk: ModelCaps; pi: ModelCaps }>();
  for (const m of rows) {
    const b = baseModelId(m.modelId);
    const entry = map.get(b) ?? { sdk: undefined, pi: resolvePiWireRow(rows, b)?.capabilities };
    if (m.protocol === "anthropic_messages" && !entry.sdk && m.capabilities) entry.sdk = m.capabilities;
    map.set(b, entry);
  }
  return map;
}

// ---------- 候选派生（红线 #9 单源，RolesPanel 与统一治理面共用，禁复制） ----------

export interface ScopedModelCandidates {
  /** 平台供给全量（主脑 / 工具），全员默认 scope 的候选。 */
  brainSupply: EnterpriseAdminBundleRawModel[];
  toolSupply: EnterpriseAdminBundleRawModel[];
  /** 角色 scope 候选 = org 圈定集（org 未圈某线 → 该线平台供给全量）。 */
  brainForRole: EnterpriseAdminBundleRawModel[];
  toolForRole: EnterpriseAdminBundleRawModel[];
}

/** 从 org bundle + org modelSelection 派生四类候选（主脑/工具 × 全员默认/角色）。角色候选先被
 *  org 圈定收窄（三层漏斗：平台 → org → role）。orgSelection 传 raw（parseModelSelection guard）。 */
export function deriveScopedCandidates(
  bundle: EnterpriseAdminBundleRaw | null,
  orgSelection: unknown,
): ScopedModelCandidates {
  const deduped = bundle ? dedupeModelsById(bundle.models) : [];
  const brainSupply = deduped.filter((m) => isBrainCatalogEntry(m));
  const toolSupply = deduped.filter((m) => !isBrainCatalogEntry(m));
  const orgSel = parseModelSelection(orgSelection);
  const inSet = (allowed: string[] | undefined, m: EnterpriseAdminBundleRawModel): boolean =>
    !allowed || allowed.includes(m.modelId);
  return {
    brainSupply,
    toolSupply,
    brainForRole: brainSupply.filter((m) => inSet(orgSel?.brainModels, m)),
    toolForRole: toolSupply.filter((m) => inSet(orgSel?.toolModels, m)),
  };
}

/** 候选行 → 去重的 base id（保持首次出现顺序）。 */
function candidateBases(models: readonly EnterpriseAdminBundleRawModel[]): string[] {
  return [...new Set(models.map((m) => baseModelId(m.modelId)))];
}

/**
 * 面板侧的主脑圈定「继承减排除」（N-87）：与下发（admin-portal bootstrap）**同一个**纯函数
 * `resolveBrainSelectionAgainstCandidates`，面板看到的勾选 = 员工拿到的圈定。
 *  - 候选 = 本 scope 可见候选里属于主脑池的行（`isBrainPoolEntry`，与下发侧「平台 Claude 主脑池」同一判据）；
 *  - 下线 = 全员默认候选（平台供给给本企业的主脑行）里一行都没有；供给为空（不可信）→ 原样返回。
 */
export function resolvePanelBrainSelection(
  selection: ModelSelection | undefined,
  scopeCandidates: readonly EnterpriseAdminBundleRawModel[],
  brainSupply: readonly EnterpriseAdminBundleRawModel[],
): BrainSelectionResolution {
  if (brainSupply.length === 0) return { selection, removed: [], retained: [], added: [] };
  const supplyBases = new Set(candidateBases(brainSupply));
  return resolveBrainSelectionAgainstCandidates(
    selection,
    candidateBases(selectableBrainCandidates(scopeCandidates)),
    (modelId) => supplyBases.has(baseModelId(modelId)),
  );
}

/**
 * 本 scope **此刻能勾**的主脑候选行（N-115，与平台层 `brainPoolCandidates` 同口径）：属于主脑池
 * （`isBrainPoolEntry`）且上游不是显式不可用。`available === false` 的行本页复选框本来就禁用，
 * 自动纳入与「见过的候选」都不该把它算进去；字段缺席 = unknown = 仍是候选。
 */
function selectableBrainCandidates(models: readonly EnterpriseAdminBundleRawModel[]): EnterpriseAdminBundleRawModel[] {
  return models.filter((m) => isBrainPoolEntry(m) && m.available !== false);
}

// ---------- 统一「模型治理」tab ----------

/** 当前编辑 scope：全员默认（org 层）或某角色（role.dosia.modelSelection 层）。 */
type GovScope = { kind: "org" } | { kind: "role"; roleId: string };

/** modelSelection 写 body（org PUT / role PATCH 共用形状；空即省略 = 继承上层，红线 #9）。 */
interface SelectionBody {
  brainModels?: string[];
  /** 保存那一刻本 scope 可选的全部主脑候选 base id（N-87，只随 brainModels 提交）。 */
  reviewedBrainModels?: string[];
  toolModels?: string[];
  modelParams?: Record<string, ModelParamsByChain>;
}

function ModelGovernanceTab({
  orgCode,
  token,
  bundle,
}: {
  orgCode: string;
  token: string;
  bundle: EnterpriseAdminBundleRaw | null;
}): JSX.Element {
  const orgSelQ = useApiQuery<{ modelSelection: ModelSelection | null }>(
    modelSelectionPath(orgCode),
    [orgCode],
  );
  const rolesQ = useApiQuery<PublicRole[]>(rolesPath(orgCode), [orgCode]);

  const [scope, setScope] = useState<GovScope>({ kind: "org" });
  // 三态可见性（null = 该线继承上层）+ 参数覆盖（base id → 参数）。切 scope 时由下方 effect 重灌。
  const [brainLocal, setBrainLocal] = useState<string[] | null>(null);
  // 当前 scope 已保存的圈定（主脑线 N-87 对账的基线：见过的候选集、哪些是自动纳入的都从它算）。
  const [brainBaseline, setBrainBaseline] = useState<ModelSelection | undefined>(undefined);
  const [toolLocal, setToolLocal] = useState<string[] | null>(null);
  // 参数覆盖：base id → per-chain 范围/默认（Model Z，PR-2）。直接持 ModelParamsByChain（不再扁平
  // 互转）——ChainParamRangeEditor 消费/回写，save 时原样喂 modelParams（PR-1 已接住 per-chain 形）。
  const [params, setParams] = useState<Record<string, ModelParamsByChain>>({});
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  // role scope 保存后调 rolesQ.refetch() 刷新缓存（供切走再切回时重灌用最新值）；该 refetch
  // 会触发下面的 rehydrate effect（rolesQ.data 引用变化），但本地态在 save() 里已由 PATCH
  // 回执直接写入（镜像 org 分支），这次自触发的重灌只会把刚设的 saved=true 冲掉——用一次性
  // 消费的 ref 让 effect 跳过这次自己制造的回声（2026-07-23 e2e 实测：role scope 保存后
  // 「已保存」提示一闪而过，根因即此）。
  const suppressNextRoleRehydrateRef = useRef(false);
  // org scope 同理：保存后必须 refetch orgSelQ——它不止喂本 scope 的重灌，还是角色 scope 候选
  // （orgBrainEffective）与「云端任务模型清单」卡片的数据源；只落本地态不刷新，切到角色页会拿
  // 保存前的全员默认推候选，把刚排除的模型当「新上线」再纳入一次（N-87 本地 e2e 实测）。
  const suppressNextOrgRehydrateRef = useRef(false);

  const roles = rolesQ.data ?? [];
  const activeRole = scope.kind === "role" ? roles.find((r) => r.id === scope.roleId) : undefined;

  // 全员默认的主脑圈定按 N-87 对账后的有效集——角色 scope 的候选从它收窄（与下发侧角色候选同形）。
  const orgBrainEffective = useMemo(() => {
    const brainSupply = deriveScopedCandidates(bundle, undefined).brainSupply;
    return resolvePanelBrainSelection(parseModelSelection(orgSelQ.data?.modelSelection), brainSupply, brainSupply).selection;
  }, [bundle, orgSelQ.data]);
  const candidates = useMemo(
    () => deriveScopedCandidates(bundle, orgBrainEffective),
    [bundle, orgBrainEffective],
  );
  const brainCandidates = scope.kind === "org" ? candidates.brainSupply : candidates.brainForRole;
  const toolCandidates = scope.kind === "org" ? candidates.toolSupply : candidates.toolForRole;

  // 回声标记只对「同一 scope 内 save() 自触发的那次 refetch」有效。refetch 失败时（useApiQuery 吞错、
  // 不换 data）重灌 effect 不触发，标记会悬挂；换 scope 时先清掉，免得下次切回时误吞一次真重灌、
  // 显示上一个 scope 的本地态。必须声明在下面的重灌 effect 之前（同一次提交里 effect 按声明序执行）。
  useEffect(() => {
    suppressNextOrgRehydrateRef.current = false;
    suppressNextRoleRehydrateRef.current = false;
  }, [scope]);

  // 当前 scope 存储态 → 本地态（org: org.modelSelection；role: role.dosia.modelSelection）。
  // 依赖 scope + 两 query，切 scope / 保存回执刷新时重灌。**query 未加载（data===undefined）即返回**
  // ——不拿 transient null 清空本地态（否则加载首帧误显「全继承」，后被真值覆盖，制造闪烁 + 测试竞态）。
  useEffect(() => {
    let raw: unknown;
    if (scope.kind === "org") {
      if (!orgSelQ.data) return; // 未加载（null/undefined）→ 不覆盖；已加载即便 {modelSelection:null} 也是真值对象
      if (suppressNextOrgRehydrateRef.current) {
        suppressNextOrgRehydrateRef.current = false;
        return; // save() 自己触发的 refetch 回声，本地态已由 PUT 回执落定
      }
      raw = orgSelQ.data.modelSelection;
    } else {
      if (!rolesQ.data) return;
      if (suppressNextRoleRehydrateRef.current) {
        suppressNextRoleRehydrateRef.current = false;
        return; // 本次是 save() 自己触发的 refetch 回声，本地态已同步，不重灌不冲 saved
      }
      raw = (activeRole?.dosia as { modelSelection?: unknown } | undefined)?.modelSelection;
    }
    const sel = parseModelSelection(raw);
    setBrainBaseline(sel);
    // 勾选态 = 员工实际拿到的圈定（N-87）：已保存的列表 + 自动纳入的新候选。下线的先留在本地、
    // 由提示条标出，保存时剔除（剔空保护同下发）。
    const brainAdded = resolvePanelBrainSelection(sel, brainCandidates, candidates.brainSupply).added;
    setBrainLocal(sel?.brainModels ? [...sel.brainModels, ...brainAdded] : null);
    setToolLocal(sel?.toolModels ?? null);
    setParams(sel?.modelParams ?? {});
    setSaved(false);
    setSaveError(null);
    // activeRole 由 scope + rolesQ.data 派生，二者即依赖；候选由 bundle + orgSelQ.data 派生。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, orgSelQ.data, rolesQ.data, bundle]);

  // 主脑线提示（N-87）：自动纳入的新候选（仍勾着的）与已下线的 id（仍在列表里的）。
  const brainResolution = useMemo(
    () => resolvePanelBrainSelection(brainBaseline, brainCandidates, candidates.brainSupply),
    [brainBaseline, brainCandidates, candidates.brainSupply],
  );
  const supplyBases = useMemo(() => new Set(candidateBases(candidates.brainSupply)), [candidates.brainSupply]);
  const isRetiredBrain = (modelId: string): boolean =>
    supplyBases.size > 0 && !supplyBases.has(baseModelId(modelId));
  const autoAddedBrain = brainLocal ? brainResolution.added.filter((id) => brainLocal.includes(id)) : [];
  const retiredBrain = brainLocal ? brainLocal.filter(isRetiredBrain) : [];
  const brainLabel = (modelId: string): string =>
    candidates.brainSupply.find((m) => baseModelId(m.modelId) === baseModelId(modelId))?.displayName || modelId;
  // per-chain 协议专属能力天花板（从**未折叠**的 raw bundle.models 建，方案 §2 Codex 修 #6）。
  const chainCapsByBase = useMemo(() => buildChainCapsByBase(bundle?.models), [bundle]);

  const touch = (): void => setSaved(false);
  const setBrain = (next: string[] | null): void => {
    setBrainLocal(next);
    touch();
  };
  const setTool = (next: string[] | null): void => {
    setToolLocal(next);
    touch();
  };
  // 参数覆盖 upsert（ChainParamRangeEditor 回写整份 per-chain 对象；undefined = 全清回落继承 → 删 key）。
  const setModelParam = (base: string, next: ModelParamsByChain | undefined): void => {
    setParams((cur) => {
      const out = { ...cur };
      if (next) out[base] = next;
      else delete out[base];
      return out;
    });
    touch();
  };

  const buildSelectionBody = (): SelectionBody => {
    const body: SelectionBody = {};
    if (brainLocal !== null) {
      // 已下线的 id 不提交（N-87）；剔空保护同下发——全是失效 id 时原样提交，空列表 = 继承全量会翻转含义。
      const alive = brainLocal.filter((id) => !isRetiredBrain(id));
      body.brainModels = alive.length > 0 ? alive : brainLocal;
      // 这次见过的全部候选：下发时不在这里的新候选自动纳入，在这里却没勾的 = 管理员排除。
      // 候选为空（企业模型通道拿不到 / 组织未接管）时不能提交空集——服务端会把 [] 清成「没见过」，
      // 下发按 d2 把管理员排除过的模型再并回来；此时沿用已存的见过集合（对齐 N-75 超管页「清单
      // 不是 live 时不提交候选集」）。
      // N-115：只记此刻能勾的候选（上游不提供的不算见过，恢复后按新上纳入），并保留上次记录里仍在
      // 供给里的（可选时明确取消过的，不因维护期保存被忘掉）——与平台页 rememberReviewedModels 同口径。
      const keep = brainBaseline?.reviewedBrainModels;
      const seen = brainCandidates.length > 0
        ? rememberReviewedModels(keep, candidateBases(selectableBrainCandidates(brainCandidates)), brainCandidates)
        : [];
      if (seen.length > 0) body.reviewedBrainModels = seen;
      else if (keep && keep.length > 0) body.reviewedBrainModels = keep;
    }
    if (toolLocal !== null) body.toolModels = toolLocal;
    if (Object.keys(params).length > 0) body.modelParams = params;
    return body;
  };

  const save = async (): Promise<void> => {
    setBusy(true);
    setSaveError(null);
    try {
      if (scope.kind === "org") {
        // org PUT：整体替换，空线省略 = 继承。回执落定本地态。
        const r = await apiCall<{ modelSelection: ModelSelection | null }>(
          "PUT",
          modelSelectionPath(orgCode),
          { token, body: buildSelectionBody() },
        );
        const sel = parseModelSelection(r.modelSelection);
        setBrainBaseline(sel);
        setBrainLocal(sel?.brainModels ?? null);
        setToolLocal(sel?.toolModels ?? null);
        setParams(sel?.modelParams ?? {});
        suppressNextOrgRehydrateRef.current = true;
        await orgSelQ.refetch();
      } else {
        // role PATCH：**整份 dosia 回传**（路由 update.dosia = next 是整体替换，只改 modelSelection
        // 会清空其余角色配置），服务端据 modelSelection 重跑 buildModelSelection + 权威盖章 editedBy。
        const roleDosia =
          activeRole?.dosia && typeof activeRole.dosia === "object" && !Array.isArray(activeRole.dosia)
            ? (activeRole.dosia as Record<string, unknown>)
            : {};
        const r = await apiCall<PublicRole>("PATCH", roleByIdPath(orgCode, scope.roleId), {
          token,
          body: { dosia: { ...roleDosia, modelSelection: buildSelectionBody() } },
        });
        // PATCH 回执即权威值（服务端已重跑 buildModelSelection + 盖章 editedBy），直接落定本地态
        // ——镜像 org 分支，不必等 refetch 才知道保存后的值。
        const roleSel = parseModelSelection(
          (r.dosia as { modelSelection?: unknown } | undefined)?.modelSelection,
        );
        setBrainBaseline(roleSel);
        setBrainLocal(roleSel?.brainModels ?? null);
        setToolLocal(roleSel?.toolModels ?? null);
        setParams(roleSel?.modelParams ?? {});
        // rolesQ 缓存仍要刷新（供切走再切回本角色时重灌用），但这次 refetch 是本次保存自己
        // 触发的回声——标记跳过，避免下面 rehydrate effect 把刚设的 saved=true 冲掉。
        suppressNextRoleRehydrateRef.current = true;
        await rolesQ.refetch();
      }
      setSaved(true);
    } catch (err) {
      setSaveError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  if (!bundle) {
    return (
      <div className="rounded-lg border border-dosia-border bg-dosia-bg-lighter p-4 text-sm text-dosia-text-secondary">
        尚未接管 ByteSpike 模型网关。请到「网关配置」tab 接管后，即可在此按全员默认 / 各角色治理可用模型与参数。
      </div>
    );
  }

  const isRole = scope.kind === "role";
  const scopeLabel = isRole ? activeRole?.name ?? activeRole?.code ?? "角色" : "全员默认";

  return (
    <div className="flex flex-col gap-3">
      {/* scope 选择器（segmented）：全员默认 + 各角色 */}
      <div className="flex flex-wrap gap-1 rounded-md border border-dosia-border bg-dosia-bg-lighter p-1">
        <ScopeChip active={scope.kind === "org"} onClick={() => setScope({ kind: "org" })}>
          全员默认
        </ScopeChip>
        {roles.map((r) => (
          <ScopeChip
            key={r.id}
            active={isRole && scope.roleId === r.id}
            onClick={() => setScope({ kind: "role", roleId: r.id })}
          >
            {r.name}
          </ScopeChip>
        ))}
      </div>

      <p className="text-xs text-dosia-text-secondary">
        {isRole ? (
          <>
            当前编辑 <b>{scopeLabel}</b> 角色。候选来自「全员默认」圈定集；<b>留空的项自动继承全员默认</b>，
            取消勾选 = 对本角色收窄可见性，调参数 = 对本角色覆盖默认。
          </>
        ) : (
          <>
            全员默认（org 层）——本企业所有员工的模型可用集与默认参数基线。<b>某线不圈定 = 继承平台全量</b>，
            不锁人；参数留空 = 继承超管平台默认。角色可在此基础上进一步收窄 / 覆盖。
          </>
        )}
      </p>

      {saveError && <ErrorBanner onDismiss={() => setSaveError(null)}>{saveError}</ErrorBanner>}
      {saved && !saveError ? (
        <div className="rounded-md border border-emerald-400/40 bg-emerald-400/10 px-3 py-2 text-sm text-emerald-700">
          已保存
        </div>
      ) : null}

      {/* 主脑圈定跟上平台（N-87）：自动纳入的新上模型可一键取消（= 排除）；已下线的保存时移除。 */}
      {autoAddedBrain.length > 0 ? (
        <div
          data-testid="brain-auto-added-banner"
          className="flex flex-wrap items-center justify-between gap-2 rounded border border-dosia-border bg-dosia-bg-light px-3 py-2 text-xs text-dosia-text-secondary"
        >
          <span>
            <span className="font-medium text-dosia-text">
              平台新上线 {autoAddedBrain.length} 个主脑模型，已自动纳入：
            </span>
            {autoAddedBrain.map(brainLabel).join("、")}
          </span>
          <button
            type="button"
            className="text-dosia-accent hover:underline"
            onClick={() => setBrain((brainLocal ?? []).filter((id) => !autoAddedBrain.includes(id)))}
          >
            取消纳入
          </button>
        </div>
      ) : null}
      {retiredBrain.length > 0 ? (
        <div
          data-testid="brain-retired-banner"
          className="flex flex-wrap items-center justify-between gap-2 rounded border border-dosia-warning/40 bg-dosia-warning/10 px-3 py-2 text-xs text-dosia-warning"
        >
          <span>
            <span className="font-medium">列表里 {retiredBrain.length} 个模型本企业当前用不了（平台已下线，或未对本企业开放），保存时移除：</span>
            {retiredBrain.join("、")}
          </span>
          {/* 剔空保护：全是下线 id 时不给一键移除（清空 = 恢复继承全量，会翻转含义）。 */}
          {(brainLocal ?? []).length > retiredBrain.length ? (
            <button
              type="button"
              className="hover:underline"
              onClick={() => setBrain((brainLocal ?? []).filter((id) => !isRetiredBrain(id)))}
            >
              一键移除
            </button>
          ) : null}
        </div>
      ) : null}

      {/* 主脑：三态可见性 + 折叠参数编辑器（thinking/effort，capability 驱动） */}
      <CurationLine
        kind="brain"
        title={isRole ? `有效主脑模型 · ${scopeLabel}` : "主脑模型可用集"}
        candidates={brainCandidates}
        local={brainLocal}
        onChange={setBrain}
        ariaPrefix={isRole ? "role-model" : "org-model"}
        hint={
          isRole
            ? (inh, checked, total) =>
                inh
                  ? `继承全员默认（${total} 个）· 取消任一项即对本角色收窄`
                  : `本角色显式可见 ${checked}/${total} 个 · 新上的主脑模型自动纳入，取消勾选的不再加回 ·「恢复继承」回落全员默认`
            : (inh, checked, total) =>
                inh
                  ? `继承平台全量（${total} 个）· 取消任一项即切换为本企业显式圈定`
                  : `本企业显式圈定 ${checked}/${total} 个 · 平台新上的主脑模型自动纳入，取消勾选的不再加回 ·「恢复继承」清空则回落上层全量`
        }
        renderRowExtra={(m) => {
          const b = baseModelId(m.modelId);
          const caps = chainCapsByBase.get(b);
          return (
            <ChainParamRangeEditor
              base={b}
              piEligible={m.piEligible === true}
              sdkCaps={caps?.sdk ?? m.capabilities}
              piCaps={caps?.pi}
              value={params[b]}
              onChange={setModelParam}
            />
          );
        }}
      />

      {/* 工具模型（生图/看图）：只读优先级 + 三态可见性收窄，无参数编辑器（决策 D3） */}
      <CurationLine
        kind="tool"
        title={isRole ? `内置工具默认模型 · ${scopeLabel}` : "工具模型可用集"}
        candidates={toolCandidates}
        local={toolLocal}
        onChange={setTool}
        ariaPrefix={isRole ? "role-model" : "org-model"}
        hint={
          isRole
            ? (inh, checked, total) =>
                inh
                  ? `继承全员默认（${total} 个）· 顺序=优先级（超管平台单份，只读）· 取消任一项即对本角色收窄`
                  : `本角色显式可见 ${checked}/${total} 个 · 优先级只读 ·「恢复继承」回落全员默认`
            : (inh, checked, total) =>
                inh
                  ? `继承平台全量（${total} 个）· 顺序=优先级（只读）· 取消任一项即切换为本企业显式圈定`
                  : `本企业显式圈定 ${checked}/${total} 个 · 优先级只读 ·「恢复继承」回落上层全量`
        }
      />

      {!isRole ? (
        <CloudTaskModelListCard
          orgCode={orgCode}
          token={token}
          rawModels={bundle.models}
          orgSelection={orgSelQ.data?.modelSelection ?? null}
        />
      ) : null}

      <div>
        <PrimaryButton
          onClick={() => void save()}
          disabled={
            busy || (orgSelQ.loading && !orgSelQ.data) || (isRole && rolesQ.loading && !rolesQ.data)
          }
        >
          {busy ? "保存中…" : "保存"}
        </PrimaryButton>
      </div>
    </div>
  );
}

/** scope 选择器单个 chip（segmented control 内）。 */
function ScopeChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "rounded px-2.5 py-1 text-xs",
        active
          ? "bg-dosia-accent font-medium text-dosia-bg"
          : "text-dosia-text-secondary hover:bg-dosia-hover hover:text-dosia-text",
      )}
    >
      {children}
    </button>
  );
}

// ---------- 页面 ----------

type PanelTab = "governance" | "memory" | "seats" | "gateway";

export function ModelManagementPanel(): JSX.Element {
  const { org } = useOutletContext<OrgDetailContext>();
  const auth = useAtomValue(authAtom);
  // org-admin 管自己 org，super/support 代任意组织配置（canManageOrg 集中派生）。
  const canManage = canManageOrg(auth?.scope);
  const [tab, setTab] = useState<PanelTab>("governance");

  const rawQ = useApiQuery<RawResponse>(canManage ? modelsRawPath(org.code) : null, [org.code, canManage]);
  const overrideQ = useApiQuery<OverrideResponse>(
    canManage ? modelsOverridePath(org.code) : null,
    [org.code, canManage],
  );
  const membersQ = useApiQuery<MembersResponse>(
    canManage ? modelsMembersPath(org.code) : null,
    [org.code, canManage],
  );

  const refetchAll = (): void => {
    void rawQ.refetch();
    void overrideQ.refetch();
    void membersQ.refetch();
  };

  const disabled = isChannelDisabled(rawQ.data);
  const rawBundle: EnterpriseAdminBundleRaw | null =
    rawQ.data && !isChannelDisabled(rawQ.data) && "models" in rawQ.data ? rawQ.data : null;
  const overrideData: EnterpriseAdminBundleOverrideRedacted | null =
    overrideQ.data && !isChannelDisabled(overrideQ.data) && "groupOverrides" in overrideQ.data
      ? overrideQ.data
      : null;
  const members = membersQ.data && !isChannelDisabled(membersQ.data) ? membersQ.data.members : [];

  const header = (
    <div>
      <h2 className="text-lg font-semibold">企业模型</h2>
      <p className="text-sm text-dosia-text-secondary">
        接管 ByteSpike 模型网关、覆盖 org 级模型凭证并下发全员桌面端。密钥绝不明文落盘，全部服务端加密托管。
      </p>
    </div>
  );

  return (
    <div className="space-y-4">
      {header}

      {!canManage ? (
        <div className="max-w-2xl rounded-lg border border-dosia-border bg-dosia-bg-lighter p-4 text-sm text-dosia-text-secondary">
          请先登录管理员账号后管理组织级模型配置。
        </div>
      ) : disabled ? (
        <div className="max-w-2xl rounded-lg border border-dosia-border bg-dosia-bg-lighter p-4 text-sm text-dosia-text-secondary">
          企业模型管理通道未配置，请联系 DOSIA 运营。
        </div>
      ) : (
        <>
          {rawQ.error && <ErrorBanner>{rawQ.error.message}</ErrorBanner>}
          {rawQ.loading && !rawQ.data ? (
            <p className="text-sm text-dosia-text-dim">加载中…</p>
          ) : auth ? (
            <>
              <div className="flex border-b border-dosia-border">
                {(
                  [
                    ["governance", "模型治理", false],
                    ["memory", "记忆与预算", false],
                    ["seats", "席位总览", true],
                    ["gateway", "网关配置", false],
                  ] as const
                ).map(([key, label, isNew]) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={tab === key}
                    onClick={() => setTab(key)}
                    className={cn(
                      "px-3 py-1.5 text-sm border-b-2 -mb-px",
                      tab === key
                        ? "border-dosia-accent text-dosia-text"
                        : "border-transparent text-dosia-text-secondary hover:text-dosia-text",
                    )}
                  >
                    {label}
                    {isNew ? (
                      <span className="ml-1.5 rounded bg-dosia-accent px-1 py-0.5 align-middle text-[10px] font-semibold text-dosia-bg">
                        NEW
                      </span>
                    ) : null}
                  </button>
                ))}
              </div>

              {tab === "governance" ? (
                <ModelGovernanceTab
                  orgCode={org.code}
                  token={auth.token}
                  bundle={rawBundle}
                />
              ) : tab === "memory" ? (
                <MemoryTab orgCode={org.code} token={auth.token} />
              ) : tab === "seats" ? (
                <SeatOverviewTab orgCode={org.code} />
              ) : (
                <div className="space-y-4 max-w-2xl">
                  <TakeoverCard
                    orgCode={org.code}
                    token={auth.token}
                    bundle={rawBundle}
                    onChanged={refetchAll}
                  />
                  {rawBundle && (
                    <>
                      <OverrideEditor
                        orgCode={org.code}
                        token={auth.token}
                        bundle={rawBundle}
                        override={overrideData}
                        onSaved={refetchAll}
                      />
                      <BroadcastCard orgCode={org.code} token={auth.token} />
                      <MembersTable members={members} />
                    </>
                  )}
                </div>
              )}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}
